import { Parser } from "@siteimprove/alfa-parser";
import { Math } from "../../calculation/index.js";
import { Length as BaseLength } from "../../calculation/numeric/index.js";
import {} from "../../syntax/index.js";
import { Converter, Unit } from "../../unit/index.js";
import { Dimension } from "./dimension.js";
const { either, map } = Parser;
/**
 * @public
 */
export var Length;
(function (Length) {
    /**
     * Lengths that are the result of a calculation.
     */
    class Calculated extends Dimension.Calculated {
        static of(value) {
            return new Calculated(value);
        }
        constructor(math) {
            super(math, "length");
        }
        hasCalculation() {
            return true;
        }
        resolve(resolver) {
            return Fixed.of(this._math
                .resolve(toExpressionResolver(resolver))
                // Since the expression has been correctly typed, it should always resolve.
                .getUnsafe(`Could not resolve ${this._math} as a length`));
        }
        equals(value) {
            return value instanceof Calculated && super.equals(value);
        }
    }
    Length.Calculated = Calculated;
    /**
     * Lengths that are a fixed (not calculated) value.
     */
    class Fixed extends Dimension.Fixed {
        static of(value, unit) {
            if (typeof value === "number") {
                // The overloads ensure that unit is not undefined
                return new Fixed(value, unit);
            }
            return new Fixed(value.value, value.unit);
        }
        constructor(value, unit) {
            super(value, unit, "length");
        }
        hasCalculation() {
            return false;
        }
        hasUnit(unit) {
            return this._unit === unit;
        }
        withUnit(unit) {
            if (this.hasUnit(unit)) {
                return this;
            }
            if (Unit.isAbsoluteLength(unit) && Unit.isAbsoluteLength(this._unit)) {
                return Fixed.of(Converter.length(this._value, this._unit, unit), unit);
            }
            throw new Error(`Cannot convert ${this._unit} to ${unit}`);
        }
        isRelative() {
            return Unit.isRelativeLength(this._unit);
        }
        isFontRelative() {
            return Unit.isFontRelativeLength(this._unit);
        }
        isViewportRelative() {
            return Unit.isViewportRelativeLength(this._unit);
        }
        isAbsolute() {
            return Unit.isAbsoluteLength(this._unit);
        }
        scale(factor) {
            return new Fixed(this._value * factor, this._unit);
        }
        /**
         * Resolve a Length into an absolute Length in pixels.
         */
        resolve(resolver) {
            return this.isRelative()
                ? resolver.length(this)
                : this.withUnit(Unit.Length.Canonical);
        }
        /**
         * @internal
         */
        toBase() {
            return BaseLength.of(this._value, this._unit);
        }
        equals(value) {
            return value instanceof Fixed && super.equals(value);
        }
    }
    Length.Fixed = Fixed;
    /**
     * @internal
     */
    function toExpressionResolver(resolver) {
        return resolver?.length === undefined
            ? {}
            : { length: (length) => resolver.length(Fixed.of(length)).toBase() };
    }
    Length.toExpressionResolver = toExpressionResolver;
    /**
     * Build a (fixed) length resolver, using basis for the relative units
     */
    function resolver(emBase, remBase, vwBase, vhBase) {
        return (length) => {
            const { unit, value } = length;
            const [min, max] = vhBase.value < vwBase.value ? [vhBase, vwBase] : [vwBase, vhBase];
            switch (unit) {
                // https://www.w3.org/TR/css-values/#em
                case "em":
                    return emBase.scale(value);
                // https://www.w3.org/TR/css-values/#rem
                case "rem": {
                    return remBase.scale(value);
                }
                // https://www.w3.org/TR/css-values/#ex
                case "ex":
                // https://www.w3.org/TR/css-values/#ch
                case "ch":
                    return emBase.scale(value * 0.5);
                // https://www.w3.org/TR/css-values/#vh
                case "vh":
                    return vhBase.scale(value / 100);
                // https://www.w3.org/TR/css-values/#vw
                case "vw":
                    return vwBase.scale(value / 100);
                // https://www.w3.org/TR/css-values/#vmin
                case "vmin":
                    return min.scale(value / 100);
                // https://www.w3.org/TR/css-values/#vmax
                case "vmax":
                    return max.scale(value / 100);
            }
        };
    }
    Length.resolver = resolver;
    function isLength(value) {
        return value instanceof Calculated || value instanceof Fixed;
    }
    Length.isLength = isLength;
    function isCalculated(value) {
        return value instanceof Calculated;
    }
    Length.isCalculated = isCalculated;
    function isFixed(value) {
        return value instanceof Fixed;
    }
    Length.isFixed = isFixed;
    function of(value, unit) {
        if (typeof value === "number") {
            // The overloads ensure that unit is not undefined
            return Fixed.of(value, unit);
        }
        if (BaseLength.isLength(value)) {
            return Fixed.of(value.value, value.unit);
        }
        return Calculated.of(value);
    }
    Length.of = of;
    /**
     * {@link https://drafts.csswg.org/css-values/#lengths}
     */
    Length.parse = either(map(BaseLength.parse, of), map(Math.parseLength, of));
})(Length || (Length = {}));
//# sourceMappingURL=length.js.map